import React from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import PageHero from '@/components/PageHero';
import { LinkButton } from '@/components/Button';
import { Title } from '@/components/Title';
import { ArrowRight, Eye, BarChart, ShieldCheck, CircleCheckBig, Briefcase, PiggyBank, TrendingUp } from 'lucide-react';

const processSteps = [
  {
    step: 1,
    icon: Briefcase,
    label: 'EXPLORE',
    title: 'Explore Investment Opportunities',
    description: 'Whether you\'re looking to generate income, grow your portfolio\'s value, or a combination of both, we offer investments that match these objectives.',
    features: [
      { title: 'Broadest Range', description: 'We offer more alternative asset classes than any other platform.' },
      { title: 'Top-Tier Investment Managers', description: 'Access institutional-quality investments without institutional costs.' },
    ],
    imageUrl: 'https://images.pexels.com/photos/186461/pexels-photo-186461.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    cta1: { text: 'Browse Investments', href: '/investments' },
    cta2: { text: 'Get Recommendations', href: 'https://app.alta-x.com/signup' },
  },
  {
    step: 2,
    icon: PiggyBank,
    label: 'INVEST',
    title: 'Invest with Confidence',
    description: 'From past performance metrics to background on our partners, we aim to provide all the information needed to make an informed decision.',
    features: [
      { title: 'Highly-Vetted', description: 'All investments pass a four-step due diligence process.' },
      { title: 'Dedicated Support', description: 'Our investor relations team is available to answer your questions at any time.' },
    ],
    imageUrl: 'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    cta1: { text: 'Learn More', href: '#due-diligence' },
  },
  {
    step: 3,
    icon: TrendingUp,
    label: 'EARN',
    title: 'Track Your Performance and Earnings',
    description: 'Investments typically pay regular income, growth at maturity, or a combination of both.',
    features: [
      { title: 'See Your Performance', description: 'We provide regular updates on your investment.' },
      { title: 'Returns on Your Returns', description: 'Roll your maturing investments directly into a new opportunity.' },
    ],
    imageUrl: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    cta1: { text: 'Get Started', href: 'https://app.alta-x.com/signup' },
  },
];

const diligenceItems = [
    {
        icon: Eye,
        title: 'Curate Exclusive Opportunities',
        description: 'We partner with third-party managers to bring you opportunities normally only available to institutions and have developed an extensive network of partnerships with experienced originators.'
    },
    {
        icon: ShieldCheck,
        title: 'Holistic Due Diligence Review',
        description: 'Opportunities are evaluated first by a dedicated team for each asset class. From there, committees unassociated with the opportunity provide a fresh perspective and challenge the investment\'s thesis to identify any potential risks.'
    },
    {
        icon: BarChart,
        title: 'Ongoing Monitoring and Reporting',
        description: 'Our experienced Portfolio & Risk Management Team constantly monitors your investments, providing regular updates. You can view your portfolio and track your performance at any time on the ALTA-X app.'
    }
];

const HowItWorks = () => {
  const containerVariants: Variants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' }
    }
  };

  return (
    <div className="bg-slate-50">
      <PageHero 
        title="How It Works"
        subtitle="Follow our straightforward process to explore, invest, and grow your wealth with alternative assets."
        backgroundImage="https://images.pexels.com/photos/1611974789855/pexels-photo-1611974789855.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
      />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Vertical Stepper Line */}
            <div className="hidden md:block absolute top-12 bottom-12 left-1/2 -translate-x-1/2 w-0.5 bg-gradient-to-b from-transparent via-[hsl(var(--navy-light))] to-transparent"></div>

            <div className="space-y-28">
              {processSteps.map((step, index) => (
                <motion.div 
                  key={step.step} 
                  className={`relative grid md:grid-cols-2 gap-x-16 gap-y-12 items-center`}
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <div className={`space-y-6 ${index % 2 !== 0 ? 'md:order-2' : ''}`}>
                    <div className="flex items-center gap-4">
                      <div className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[hsl(var(--navy))] to-[hsl(var(--navy-light))] shadow-lg ring-4 ring-slate-50">
                        <step.icon className="w-8 h-8 text-white" />
                      </div>
                      <Title as="h2" className="!text-4xl">
                        {step.title}
                      </Title>
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed border-l-4 border-slate-200 pl-6 py-2">{step.description}</p>
                    <ul className="space-y-4 pt-4">
                      {step.features.map(feature => (
                        <li key={feature.title} className="flex items-start p-4 bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-300 hover:shadow-md transition-all">
                          <CircleCheckBig className="w-7 h-7 text-emerald-500 mr-4 mt-1 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-gray-900 text-lg">{feature.title}</h4>
                            <p className="text-slate-600">{feature.description}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-wrap gap-4 pt-4">
                      <LinkButton to={step.cta1.href} variant="solid" size="lg" className="group">
                        <span>{step.cta1.text}</span>
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                      </LinkButton>
                      {step.cta2 && (
                        <LinkButton href={step.cta2.href} variant="outline" size="lg">{step.cta2.text}</LinkButton>
                      )}
                    </div>
                  </div>
                  <div className={`rounded-2xl overflow-hidden shadow-2xl aspect-w-4 aspect-h-3 ${index % 2 !== 0 ? 'md:order-1' : ''}`}>
                    <motion.img 
                      src={step.imageUrl} 
                      alt={step.title} 
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4, ease: 'easeInOut' }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="due-diligence" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8 }}
          >
            <Title as="h2">Behind the Scenes</Title>
            <p className="text-lg text-slate-600 mt-4">We analyze billions of dollars worth of investments on your behalf. Before an investment reaches your portfolio, our investment team works behind the scenes to bring what we believe are the best possible opportunities to you.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {diligenceItems.map((item, index) => (
              <motion.div 
                key={item.title} 
                className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-200/80 transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                    <item.icon className="w-8 h-8 text-[hsl(var(--navy))]" />
                </div>
                <Title as="h3" className="!text-xl mb-2">{item.title}</Title>
                <p className="text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="bg-gradient-to-br from-[hsl(var(--navy))] to-[hsl(var(--navy-dark))]">
        <div className="max-w-4xl mx-auto text-center py-20 px-4 sm:px-6 lg:px-8">
          <Title as="h2" className="text-white !text-4xl">Evolve your wealth</Title>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mt-4 mb-8">
            Invest in curated private market alternatives from top-tier managers — all on one easy-to-use platform.
          </p>
          <LinkButton
            href="https://app.alta-x.com/signup"
            variant="solid"
            size="lg"
            colorScheme="inverted"
            className="group"
          >
            <span>Get Started</span>
            <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
          </LinkButton>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
